import type { CustomFieldKind, ItemFieldEntry } from '@cheqroom/api';

import type { InfoField } from '../Fields.types';

const hasNameProperty = (value: unknown): value is { name: unknown } =>
	typeof value === 'object' && value != null && 'name' in value;

/**
 * Unwraps a raw entity property into a displayable value: an array (e.g. `codes`) becomes its
 * first element, an object with a `name` (e.g. `kit`, `category`, `custody`) becomes that name,
 * so it shows a label instead of `[object Object]`. Anything else passes through as-is.
 */
const toDisplayValue = (rawValue: unknown): unknown => {
	if (Array.isArray(rawValue)) return rawValue[0];
	if (hasNameProperty(rawValue)) return rawValue.name;
	return rawValue;
};

/**
 * Reads one system field off the entity. An Info type passes its own reader for ids that are not a
 * plain property (item `custody`, kit `description`) and must also say they resolve (`hasField`).
 */
export interface SystemFieldReader<T> {
	readField?: (entity: T, id: string) => unknown;
	hasField?: (entity: T, id: string) => boolean;
}

const readProperty = (entity: unknown, id: string): unknown => (entity as Record<string, unknown>)[id];

/**
 * A reader for an entity whose field ids differ from its property names, e.g. a booking's `from`/`to`/`contact`
 * (`fromDate`/`toDate`/`customer` on a reservation). `propertyByFieldId` is the API's `fieldIdsByName`; ids not
 * in it read the property of the same name.
 */
export const createPropertyFieldReader = <T extends object>(
	propertyByFieldId: Readonly<Record<string, string>>
): SystemFieldReader<T> => {
	const toProperty = (id: string) => (Object.hasOwn(propertyByFieldId, id) ? propertyByFieldId[id] : id);
	return {
		readField: (entity, id) => readProperty(entity, toProperty(id)),
		hasField: (entity, id) => toProperty(id) in entity,
	};
};

export const resolveSystemFields = <T extends object>(
	entity: T,
	customFields: ItemFieldEntry[] = [],
	{ readField = readProperty, hasField = (value, id) => id in value }: SystemFieldReader<T> = {}
): InfoField[] =>
	customFields
		.filter((field) => !field.id.startsWith('fields.') && hasField(entity, field.id))
		.map((field) => ({
			id: field.id,
			value: toDisplayValue(readField(entity, field.id)),
			type: field.editor,
			kind: field.kind as CustomFieldKind | undefined,
		}));
